import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const MainframeComputing = () => {
  const phrases = [
    "Enterprise System Operations",
    "Mainframe Infrastructure"
  ];

  const cardData = [
    { type: 'purple-clay', content: '🖥️' },
    { type: 'smooth-wave', content: '🗄️' },
    { type: 'abstract-blocks', content: '🧱' },
    { type: 'pink-sphere', content: '⚙️' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🖥️", title: "Mainframe Operations & Management", desc: "Maintain absolute runtime performance of core IBM z/OS operating nodes, workload schedulers, and sub-second batch environments." },
    { icon: "🔄", title: "Legacy Code Optimization", desc: "Refactor, audit, and maintain dense business logic trees written in COBOL, PL/I, and Assembler routines to maximize execution metrics." },
    { icon: "🔗", title: "Hybrid Core System Bridging", desc: "Expose secure, real-time z/OS transactions to modern public cloud front-ends using high-throughput RESTful and gRPC API adapters." },
    { icon: "🗄️", title: "High-Volume Database Care", desc: "Optimize high-tier relational Db2 structures, hierarchical IMS models, and complex VSAM storage clusters for sub-millisecond querying." },
    { icon: "⚙️", title: "CICS & IMS Transaction Control", desc: "Configure resilient runtime monitors to reliably support millions of daily critical enterprise data interactions with zero fault vectors." },
    { icon: "🛠️", title: "Automated Mainframe DevSecOps", desc: "Inject modern pipeline practices, version control tracking, and structural test triggers directly into z/OS engineering frameworks." },
    { icon: "🛡️", title: "RACF Security & Cryptography", desc: "Enforce ironclad identity boundaries, deep operational trace logging, and pervasive z16 hardware encryption layers." }
  ];

  const advancedServices = [
    { icon: "📡", title: "Real-Time Telemetry Tracking", desc: "Establish continuous behavior alerts and deep system capacity checks across processing pools via modern analytics loops." },
    { icon: "☁️", title: "Application Rehosting Traces", desc: "Relocate core processing routines safely out of physical nodes into managed cloud emulator engines with zero functional drops." },
    { icon: "📦", title: "Linux on Z Container Systems", desc: "Deploy minimized Linux microservices right inside specialized hardware cores to scale efficiency next to backend records." },
    { icon: "📈", title: "Batch Window Tuning Scans", desc: "Audit heavy nightly execution files, refine JCL structures, and adjust job tracking lines to dramatically lower runtime overhead." },
    { icon: "🧬", title: "Disaster Recovery & Hot Syncs", desc: "Structure comprehensive GDPS multi-site configurations and automated data mirror protocols for immediate failure recoveries." },
    { icon: "🤖", title: "AI-Powered Log Analysis", desc: "Utilize smart machine learning heuristics to automatically detect background software anomalies before system performance dips." },
    { icon: "💼", title: "MIPS Cost-Efficiency Reviews", desc: "Optimize computing usage lines, configure smart dynamic capacity controls, and drop ongoing software operational costs." },
    { icon: "🚦", title: "Enterprise Message Queue Routing", desc: "Unify external functional modules with local backend layers using highly resilient IBM MQ structural connections." },
    { icon: "🧪", title: "Regression Testing Orchestration", desc: "Construct end-to-end processing evaluation suites to verify backend stability during complex configuration changes." }
  ];

  const technologies = {
    "Operating Systems": ["IBM z/OS Platforms", "Linux on IBM Z (z/VM)", "z/VSE Core Architectures", "Enterprise Virtualization"],
    "Languages & JCL": ["COBOL / PL/I / Assembler", "JCL (Job Control Language)", "REXX Scripting Formats", "Java / Python on z/OS"],
    "Data & Transactions": ["IBM Db2 Relational Layer", "IMS DB/DC Frameworks", "VSAM Data Clusters", "CICS Transaction Monitors"],
    "Modernization Tools": ["IBM MQ Message Brokers", "z/OS Connect API Adaptor", "Host Integration Server", "Micro Focus Enterprise Suite"],
    "Security & Controls": ["RACF Access Governance", "IBM Z Pervasive Encryption", "Top Secret Security (TSS)", "BMC Mainview / Omegamon"]
  };

  const processSteps = [
    { num: "01", title: "System Footprint Discovery", desc: "Auditing complete MIPS capacity lines, mapping interlocking application profiles, and tracking runtime bottlenecks." },
    { num: "02", title: "Strategy Engineering", desc: "Designing customized modern architecture treatments including re-hosting blueprints, API wraps, or refactoring loops." },
    { num: "03", title: "Environment Provisioning", desc: "Setting up secure sandbox testing frameworks, pipeline automation tracks, and companion data staging targets." },
    { num: "04", title: "Code & Database Optimization", desc: "Refactoring legacy processing algorithms, tuning heavy JCL routines, and normalising core relational tables." },
    { num: "05", title: "Rigorous Regression Audits", desc: "Running deep data comparison validation routines to verify absolute logical alignment between legacy and modernized targets." },
    { num: "06", title: "Zero-Downtime Migration", desc: "Executing safe database syncing, live transaction cuts, and DNS routing changes with zero operational disruption." },
    { num: "07", title: "Capacity Fine-Tuning", desc: "Reviewing active system telemetry metrics, balancing processor compute distributions, and maintaining SLA boundaries." }
  ];

  const industries = [
    "Global Banking Infrastructure", "Credit Card Clearing Houses", "Enterprise Insurance Systems", "High-Volume Retail Networks",
    "Government Cloud Operations", "Logistics & Transit Systems", "Telecommunications Grid Core", "Healthcare Records Management",
    "Energy & Utility Suppliers", "Aviation & Airline Reservation", "Automotive & Telematics Hubs", "Capital Market Clearing"
  ];

  const designBenefits = [
    "Airtight Hardware Security", "Unrivaled Transaction Velocity", "Optimized Operational Compute Costs",
    "Seamless Modern Application Integrations", "Near-Zero Recovery Objectives", "Extensible Object Code Assets",
    "Comprehensive System Telemetry Visibility", "Absolute 99.999% Service Continuance"
  ];

  const whyChooseUs = [
    "Certified Mainframe Specialists", "Proven Zero-Downtime Data Relocations", "Modern DevSecOps on z/OS Experience",
    "Deep Understanding of Legacy Structures", "Security-First Target Node Hardening", "Optimized Workload Density Layouts",
    "Comprehensive Post-Migration Support", "Transparent MIPS Optimization Models", "Enterprise-Grade SLA Commitments",
    "Experienced Legacy Code Modernizers", "Native Hybrid Integration Paradigms", "100% Core Processing Stability Commitments"
  ];

  return (

    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Mainframe Computing Services | Mainframe Application Development & Modernization | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Mainframe Computing Services | Mainframe Application Development & Modernization | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. offers expert mainframe computing services, including mainframe application development, modernization, migration, maintenance, IBM z/OS support, COBOL development, legacy system integration, and enterprise mainframe solutions."
        />

        <meta
          name="keywords"
          content="Mainframe Computing, Mainframe Services, Mainframe Development, Mainframe Application Development, Mainframe Modernization, Mainframe Migration, IBM Mainframe, IBM z/OS, COBOL Development, Legacy System Modernization, Mainframe Maintenance, Enterprise Computing, Mainframe Integration, Mainframe Support, Mainframe Consulting, Legacy Application Migration, Enterprise IT Solutions, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/mainframe-computing"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Mainframe Computing Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Maximize the performance of your enterprise systems with mainframe computing services from Affsoft IT Solutions Pvt. Ltd. We specialize in IBM mainframe development, modernization, migration, COBOL development, and legacy system integration."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/mainframe-computing"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Mainframe Computing Services | Mainframe Development & Modernization | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Optimize your legacy enterprise systems with mainframe computing services from Affsoft IT Solutions Pvt. Ltd. We provide IBM mainframe development, modernization, migration, COBOL programming, and ongoing support."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="mainframe-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0a0d14' // dark system backdrop fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Enterprise System Operations &nbsp; and &nbsp;</span>
              <span>Mainframe Infrastructure </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-info fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Mainframe Services</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we combine the absolute dependability of traditional core processing systems with the dynamic scale of modern computing networks. Our advanced engineering pathways deliver full management optimization, secure legacy application integration, and precise cloud migrations to unlock the value of enterprise records safely.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About Affsoft</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-info fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SYSTEM SOLUTIONS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Core Architecture</span>
              <h2 className="fw-bold text-dark display-6">Uncompromising Performance Built for Global Workloads</h2>
              <p className="text-muted mt-3">
                Unoptimized legacy code structures, isolated data pipelines, and expanding MIPS operating expenses restrict strategic growth. At Affsoft IT Solutions Pvt. Ltd., we apply meticulous modern automation paradigms to scale system architectures safely.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* System Benefits Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Optimize and Modernize Enterprise Core Platforms?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED MODERNIZATION OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase">Optimization Framework</span>
              <h2 className="fw-bold text-dark display-6">Intelligent Systems Engineered for Complete Stability</h2>
              <p className="text-muted mt-3">
                We extend fundamental hosting frameworks into highly responsive digital platform systems. From batch architecture refactoring to Linux partitioning, our strategies eliminate computing friction while minimizing resource configurations.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-info">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">System Tech Stack</span>
              <h2 className="fw-bold display-6">Architectures & Enterprise Utility Libraries We Manage</h2>
              <p className="text-light-50">We run and monitor heavy transaction tracks utilizing industrial, hardened standard tools.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-info mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-info opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Execution Track</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Modernization Track</h2>
              <p className="text-muted">A clear, disciplined framework mapping isolated transaction points securely into optimized systems.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-info border-4 h-100 shadow-sm">
                    <div className="fw-bold text-info display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine programmatic regression validations with native mainframe design patterns to coordinate workloads with maximum safety.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-info fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default MainframeComputing;
